import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  Menu,
  User,
  Settings,
  LogOut,
  UserCircle2,
  ChevronRight,
  CheckCheck,
  CircleCheck,
  CircleX,
  MessageCircleWarning,
  AlertTriangle,
  Mail,
  PenSquare,
  Receipt,
  BarChart3,
  UsersRound,
  Building2,
  MessageCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/auth-context";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  ensureCrispLoaded,
  hideCrisp,
  isCrispEnabled,
  onCrispPreferenceChange,
  resetCrispSession,
  setCrispEnabled,
  setCrispSessionData,
  setCrispUser,
  showCrisp,
} from "@/lib/crisp";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from "@/lib/notifications";
import { connectRealtimeStream, type RealtimeEvent } from "@/lib/realtime";
import { queryKeys } from "@/hooks/use-app-queries";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SlideOverPanel } from "@/components/shared/SlideOverPanel";
import { PlanApprovedWorkDialog } from "@/components/schedule/PlanApprovedWorkDialog";

interface TopBarProps {
  onMenuToggle?: () => void;
}

export function TopBar({ onMenuToggle }: TopBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [workspaceName, setWorkspaceName] = useState("Workspace");
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | null>(null);
  const [workspacePlan, setWorkspacePlan] = useState<"starter" | "pro" | "team">("starter");
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const [desktopNotificationOpen, setDesktopNotificationOpen] = useState(false);
  const [mobileNotificationOpen, setMobileNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [supportChatEnabled, setSupportChatEnabled] = useState(() => isCrispEnabled());
  const [planWorkNotification, setPlanWorkNotification] = useState<NotificationItem | null>(null);
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
  const hasInteractedRef = useRef(false);
  const hasLoadedNotificationsRef = useRef(false);
  const unreadNotificationIdsRef = useRef<string[]>([]);
  const seenNotificationIdsRef = useRef<Set<string>>(new Set());
  const lastSoundPlayedAtRef = useRef(0);

  const initials = user?.fullName
    ? user.fullName
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "U";

  const roleLabel = useMemo(() => {
    if (!user?.role) return t("common.user");
    if (user.role === "ADMIN") return t("topbar.admin");
    if (user.role === "PM") return t("topbar.pm");
    return t("topbar.field");
  }, [t, user?.role]);

  useEffect(() => {
    const loadWorkspace = async () => {
      try {
        const [company, settings] = await Promise.all([
          apiRequest<{ name: string; currentPlan?: string | null }>("/companies/me", { method: "GET" }),
          apiRequest<{ companyLogoUrl?: string | null }>("/companies/me/settings", { method: "GET" }),
        ]);
        setWorkspaceName(company.name || t("topbar.workspace"));
        setWorkspaceLogoUrl(settings.companyLogoUrl || null);
        const normalizedPlan =
          company.currentPlan?.toLowerCase() === "team"
            ? "team"
            : company.currentPlan?.toLowerCase() === "pro"
              ? "pro"
              : "starter";
        setWorkspacePlan(normalizedPlan);
      } catch {
        setWorkspaceName(t("topbar.workspace"));
        setWorkspaceLogoUrl(null);
        setWorkspacePlan("starter");
      }
    };
    void loadWorkspace();
  }, [t]);

  useEffect(() => onCrispPreferenceChange(setSupportChatEnabled), []);

  useEffect(() => {
    if (import.meta.env.VITE_DISABLE_CRISP === "true") {
      hideCrisp();
      return;
    }

    if (!supportChatEnabled) {
      hideCrisp();
      return;
    }

    ensureCrispLoaded();
    showCrisp();

    if (user) {
      setCrispUser({
        email: user.email,
        name: user.fullName,
      });
      setCrispSessionData({
        workspaceId: user.companyId,
        userId: user.id,
        plan: workspacePlan,
        environment: import.meta.env.MODE,
      });
    }
  }, [supportChatEnabled, user, workspacePlan]);

  const planLabel = t(`plans.${workspacePlan}`);
  const planIntervalLabel =
    user?.planInterval === "YEARLY"
      ? t("payment.annual")
      : user?.planInterval === "MONTHLY"
        ? t("payment.monthly")
        : null;
  const planSummaryLabel = planIntervalLabel ? `${planLabel} (${planIntervalLabel})` : planLabel;
  const canUpgradePlan = workspacePlan !== "team";

  const onUpgradePlanClick = () => {
    if (!canUpgradePlan) return;
    const targetPlan = workspacePlan === "pro" ? "team" : "pro";
    navigate(`/upgrade-plan?targetPlan=${encodeURIComponent(targetPlan)}&interval=MONTHLY`);
  };

  useEffect(() => {
    const enableNotificationSound = () => {
      hasInteractedRef.current = true;
      if (!notificationAudioRef.current) {
        notificationAudioRef.current = new Audio("/sounds/notification.mp3");
        notificationAudioRef.current.preload = "auto";
      }
    };

    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart"];
    events.forEach((eventName) =>
      window.addEventListener(eventName, enableNotificationSound, { passive: true }),
    );

    return () => {
      events.forEach((eventName) =>
        window.removeEventListener(eventName, enableNotificationSound),
      );
    };
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const data = await fetchNotifications(20);
      const nextUnreadIds = data.items.filter((item) => !item.readAt).map((item) => item.id);
      const hasNewUnread = hasLoadedNotificationsRef.current
        ? nextUnreadIds.some((id) => !unreadNotificationIdsRef.current.includes(id))
        : false;

      const isFirstLoad = !hasLoadedNotificationsRef.current;
      const newItems = isFirstLoad
        ? []
        : data.items.filter((item) => !seenNotificationIdsRef.current.has(item.id));

      unreadNotificationIdsRef.current = nextUnreadIds;
      hasLoadedNotificationsRef.current = true;
      if (isFirstLoad) {
        seenNotificationIdsRef.current = new Set(data.items.map((item) => item.id));
      } else {
        data.items.forEach((item) => seenNotificationIdsRef.current.add(item.id));
      }
      setNotifications(data.items);
      setUnreadCount(data.unreadCount);

      newItems.forEach((item) => {
        if (item.type === "APPROVAL_SMS_DELIVERED" || item.type === "SCHEDULE_SMS_DELIVERED") {
          toast({
            title: item.type === "SCHEDULE_SMS_DELIVERED" ? "Schedule SMS delivered" : "SMS delivered",
            description: item.body || item.title,
          });
          return;
        }

        if (
          item.type === "APPROVAL_SMS_FAILED" ||
          item.type === "APPROVAL_SMS_UNDELIVERED" ||
          item.type === "SCHEDULE_SMS_FAILED" ||
          item.type === "SCHEDULE_SMS_UNDELIVERED"
        ) {
          const isSchedule = item.type.startsWith("SCHEDULE_");
          toast({
            title:
              item.type === "APPROVAL_SMS_FAILED" || item.type === "SCHEDULE_SMS_FAILED"
                ? isSchedule
                  ? "Schedule SMS failed"
                  : "SMS failed"
                : isSchedule
                  ? "Schedule SMS undelivered"
                  : "SMS undelivered",
            description: item.body || item.title,
            variant: "destructive",
          });
        }
      });

      if (hasNewUnread && hasInteractedRef.current) {
        const now = Date.now();
        if (now - lastSoundPlayedAtRef.current > 1500) {
          lastSoundPlayedAtRef.current = now;
          const audio = notificationAudioRef.current ?? new Audio("/sounds/notification.mp3");
          notificationAudioRef.current = audio;
          audio.currentTime = 0;
          void audio.play().catch(() => {
            // Ignore blocked playback; the bell still updates visually.
          });
        }
      }
    } catch {
      // Keep bell resilient even if notifications endpoint is temporarily unavailable.
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    void loadNotifications();
    const poll = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void loadNotifications();
    }, 10 * 60 * 1000);
    return () => window.clearInterval(poll);
  }, [loadNotifications]);

  useEffect(() => {
    const onFocus = () => {
      void loadNotifications();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadNotifications]);

  useEffect(() => {
    const onRefresh = () => {
      void loadNotifications();
    };
    window.addEventListener("workscope:notifications-refresh", onRefresh);
    return () => window.removeEventListener("workscope:notifications-refresh", onRefresh);
  }, [loadNotifications]);

  useEffect(() => {
    if (desktopNotificationOpen || mobileNotificationOpen) {
      void loadNotifications();
    }
  }, [desktopNotificationOpen, mobileNotificationOpen]);

  useEffect(() => {
    if (!user) return;

    const handleRealtimeEvent = (event: RealtimeEvent) => {
      const topics = event.topics ?? [];
      if (!topics.length) return;

      if (topics.includes("notifications")) {
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications(20) }).catch(() => undefined);
        void loadNotifications();
      }

      if (topics.includes("approvals")) {
        queryClient.invalidateQueries({ queryKey: ["approvals"] }).catch(() => undefined);
        queryClient.invalidateQueries({ queryKey: ["approval-detail"] }).catch(() => undefined);
      }

      if (topics.includes("tickets")) {
        queryClient.invalidateQueries({ queryKey: ["tickets"] }).catch(() => undefined);
        queryClient.invalidateQueries({ queryKey: ["company-profile"] }).catch(() => undefined);
      }

      if (topics.includes("dashboard")) {
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }).catch(() => undefined);
      }
    };

    const disconnect = connectRealtimeStream({
      onEvent: handleRealtimeEvent,
    });

    return disconnect;
  }, [loadNotifications, queryClient, user]);

  const onConfirmLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const onToggleSupportChat = (enabled: boolean) => {
    setSupportChatEnabled(enabled);
    setCrispEnabled(enabled);

    if (!enabled) {
      hideCrisp();
      resetCrispSession();
      return;
    }

    ensureCrispLoaded();
    showCrisp();
    if (user) {
      setCrispUser({
        email: user.email,
        name: user.fullName,
      });
      setCrispSessionData({
        workspaceId: user.companyId,
        userId: user.id,
        plan: workspacePlan,
        environment: import.meta.env.MODE,
      });
    }
  };

  const getNotificationIcon = (type: string) => {
    if (type === "PAYMENT_RECORDED") return <Receipt className="h-4 w-4 text-emerald-600" />;
    if (type === "PAYMENT_FOLLOWUP_SENT") return <Mail className="h-4 w-4 text-accent" />;
    if (type.includes("APPROVED")) return <CircleCheck className="h-4 w-4 text-emerald-600" />;
    if (type.includes("REJECTED")) return <CircleX className="h-4 w-4 text-destructive" />;
    if (type.includes("CLARIFICATION")) return <MessageCircleWarning className="h-4 w-4 text-warning" />;
    if (type.includes("OVERDUE") || type.includes("FAILED")) return <AlertTriangle className="h-4 w-4 text-warning" />;
    if (type.includes("EMAIL")) return <Mail className="h-4 w-4 text-muted-foreground" />;
    return <PenSquare className="h-4 w-4 text-muted-foreground" />;
  };

  const relativeTime = (isoDate: string) => {
    const seconds = Math.round((new Date(isoDate).getTime() - Date.now()) / 1000);
    const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
    const abs = Math.abs(seconds);
    if (abs < 60) return formatter.format(seconds, "second");
    const minutes = Math.round(seconds / 60);
    if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
    const days = Math.round(hours / 24);
    return formatter.format(days, "day");
  };

  const onNotificationClick = async (item: NotificationItem) => {
    try {
      if (!item.readAt) {
        setMarkingId(item.id);
        await markNotificationRead(item.id);
      }
    } catch {
      // Keep navigation behavior reliable even if mark-read fails.
    } finally {
      setMarkingId(null);
    }

    setNotifications((prev) =>
      prev.map((row) => (row.id === item.id ? { ...row, readAt: row.readAt ?? new Date().toISOString() } : row)),
    );
    unreadNotificationIdsRef.current = unreadNotificationIdsRef.current.filter((id) => id !== item.id);
    setUnreadCount((prev) => Math.max(0, prev - (item.readAt ? 0 : 1)));

    if (item.linkPath) {
      navigate(item.linkPath);
    } else {
      navigate("/approvals");
    }
  };

  const onScheduleFromNotification = async (item: NotificationItem) => {
    if (user?.currentPlan === "TEAM" && item.projectId && !item.projectAlreadyPlanned) {
      setPlanWorkNotification(item);
      setDesktopNotificationOpen(false);
      setMobileNotificationOpen(false);
      return;
    }

    try {
      if (!item.readAt) {
        setMarkingId(item.id);
        await markNotificationRead(item.id);
      }
    } catch {
      // Keep CTA navigation resilient even if mark-read fails.
    } finally {
      setMarkingId(null);
    }

    setNotifications((prev) =>
      prev.map((row) => (row.id === item.id ? { ...row, readAt: row.readAt ?? new Date().toISOString() } : row)),
    );
    unreadNotificationIdsRef.current = unreadNotificationIdsRef.current.filter((id) => id !== item.id);
    setUnreadCount((prev) => Math.max(0, prev - (item.readAt ? 0 : 1)));

    if (user?.currentPlan === "TEAM" && item.projectAlreadyPlanned) {
      if (item.linkPath) {
        navigate(item.linkPath);
        return;
      }
      if (item.projectId) {
        navigate(`/projects/${encodeURIComponent(item.projectId)}`);
        return;
      }
    }

    if (item.projectId) {
      navigate(`/schedule?projectId=${encodeURIComponent(item.projectId)}`);
      return;
    }

    navigate("/schedule");
  };

  const shouldShowApprovedTicketCta = (item: NotificationItem) =>
    item.type.includes("APPROVED") && Boolean(item.projectId);

  const approvedTicketCtaLabel = (item: NotificationItem) => {
    if (user?.currentPlan === "TEAM") {
      if (item.projectAlreadyPlanned) return item.linkPath ? "Review ticket" : "View project";
      return "Plan Work";
    }
    return t("schedule.actions.schedule");
  };

  const shouldShowPaymentCta = (item: NotificationItem) => item.type.startsWith("PAYMENT_");

  const markPlanningNotificationHandled = async (item: NotificationItem) => {
    try {
      if (!item.readAt) await markNotificationRead(item.id);
    } catch {
      // Keep planning resilient even if mark-read fails.
    }
    setNotifications((prev) =>
      prev.map((row) => (row.id === item.id ? { ...row, readAt: row.readAt ?? new Date().toISOString() } : row)),
    );
    unreadNotificationIdsRef.current = unreadNotificationIdsRef.current.filter((id) => id !== item.id);
    setUnreadCount((prev) => Math.max(0, prev - (item.readAt ? 0 : 1)));
  };

  const onMarkAllRead = async () => {
    try {
      setMarkingAll(true);
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, readAt: item.readAt ?? new Date().toISOString() })));
      unreadNotificationIdsRef.current = [];
      setUnreadCount(0);
      toast({ title: t("topbar.allMarkedRead") });
    } catch (error) {
      toast({
        title: t("topbar.markAllReadFailed"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setMarkingAll(false);
    }
  };

  const mobileMenuItems = [
    { label: t("nav.clients"), path: "/clients", icon: UsersRound },
    { label: t("nav.approvals"), path: "/approvals", icon: CheckCheck },
    { label: t("nav.reports"), path: "/reports", icon: BarChart3 },
    ...(user?.currentPlan === "TEAM" ? [{ label: t("nav.team"), path: "/team", icon: UsersRound }] : []),
    { label: t("nav.settings"), path: "/settings", icon: Settings },
  ];

  return (
    <header className="sticky top-0 z-20 flex items-center h-14 px-4 bg-card border-b gap-3">
      <div className="hidden md:flex items-center gap-2 min-w-0 flex-1">
        <div className="h-9 w-9 shrink-0 rounded-md border bg-muted/30 overflow-hidden flex items-center justify-center">
          {workspaceLogoUrl ? (
            <img src={workspaceLogoUrl} alt={workspaceName} className="h-full w-full object-contain" />
          ) : (
            <Building2 className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <p className="truncate text-sm font-semibold text-foreground">{workspaceName}</p>
      </div>

      <div className="hidden md:ml-auto md:flex md:items-center md:gap-2">
        <DropdownMenu open={desktopNotificationOpen} onOpenChange={setDesktopNotificationOpen}>
          <DropdownMenuTrigger asChild>
            <button className="relative p-2 rounded-md hover:bg-secondary" aria-label={t("topbar.notifications")}>
              <Bell className="h-5 w-5 text-muted-foreground" />
              {unreadCount > 0 ? <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-accent" /> : null}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 p-2">
            <div className="px-2 py-1.5 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{t("topbar.notifications")}</p>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                disabled={markingAll || unreadCount === 0}
                onClick={() => void onMarkAllRead()}
              >
                <span className="inline-flex items-center gap-1">
                  <CheckCheck className="h-3.5 w-3.5" />
                  {t("topbar.markAllRead")}
                </span>
              </button>
            </div>
            <DropdownMenuSeparator />
            {loadingNotifications ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">{t("common.loading")}</div>
            ) : notifications.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">{t("topbar.noNotifications")}</div>
            ) : (
              <div className="max-h-80 overflow-auto">
                {notifications.map((item) => (
                  <div
                    key={item.id}
                    className="px-2 py-2 rounded-md hover:bg-secondary transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 shrink-0">{getNotificationIcon(item.type)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {!item.readAt ? <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" /> : null}
                          <button
                            type="button"
                            className={`min-w-0 text-left text-sm truncate ${item.readAt ? "font-medium" : "font-semibold"}`}
                            onClick={() => void onNotificationClick(item)}
                          >
                            {item.title}
                          </button>
                          {markingId === item.id ? <span className="text-[10px] text-muted-foreground">...</span> : null}
                        </div>
                        {item.body ? (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {item.body}
                            {item.dedupCount && item.dedupCount > 1 ? ` (${item.dedupCount}x)` : ""}
                          </p>
                        ) : null}
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <p className="text-[11px] text-muted-foreground">{relativeTime(item.createdAt)}</p>
                          {shouldShowApprovedTicketCta(item) ? (
                            <button
                              type="button"
                              className="shrink-0 rounded-full border border-accent/20 bg-accent/10 px-2.5 py-1 text-[11px] font-semibold text-accent hover:bg-accent/15"
                              onClick={() => void onScheduleFromNotification(item)}
                            >
                              {approvedTicketCtaLabel(item)}
                            </button>
                          ) : shouldShowPaymentCta(item) ? (
                            <button
                              type="button"
                              className="shrink-0 rounded-full border border-accent/20 bg-accent/10 px-2.5 py-1 text-[11px] font-semibold text-accent hover:bg-accent/15"
                              onClick={() => void onNotificationClick(item)}
                            >
                              View payments
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/approvals")} className="justify-between">
              <span>{t("nav.approvals")}</span>
              <ChevronRight className="h-4 w-4" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 rounded-full bg-primary h-8 w-8 grid place-items-center"
              title={user?.fullName ?? t("common.user")}
              aria-label={t("topbar.openUserMenu")}
            >
              {user ? (
                <span className="text-xs font-semibold text-primary-foreground">{initials}</span>
              ) : (
                <User className="h-5 w-5 text-primary-foreground" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 p-2">
            <div className="px-2 py-1.5 space-y-1.5">
              <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 text-sm font-semibold text-foreground truncate">{user?.fullName || t("common.user")}</p>
                <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{roleLabel}</span>
              </div>
              <p className="text-xs text-muted-foreground truncate">{workspaceName}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email || t("common.noEmail")}</p>
              <div className="pt-1 flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">{t("common.plan")}: {planSummaryLabel}</span>
                {canUpgradePlan ? (
                  <button
                    type="button"
                    onClick={onUpgradePlanClick}
                    className="text-xs font-medium text-accent hover:underline"
                  >
                    {t("topbar.upgradePlan")}
                  </button>
                ) : null}
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <UserCircle2 className="h-4 w-4 mr-2" /> {t("topbar.profile")}
            </DropdownMenuItem>
            <div className="flex items-center justify-between gap-3 rounded-sm px-2 py-2">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <MessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{t("topbar.supportChat")}</span>
              </div>
              <Switch checked={supportChatEnabled} onCheckedChange={onToggleSupportChat} />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setConfirmLogoutOpen(true)}
            >
              <LogOut className="h-4 w-4 mr-2" /> {t("topbar.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="md:hidden flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 rounded-full bg-primary h-8 w-8 grid place-items-center"
              title={user?.fullName ?? t("common.user")}
            >
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.fullName || t("common.user")}
                  className="h-full w-full rounded-full object-cover"
                />
              ) : (
                <span className="text-xs font-semibold text-primary-foreground">{initials}</span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={8}
            collisionPadding={12}
            className="w-64 max-w-[calc(100vw-1.5rem)]"
          >
            <div className="px-2 py-2 space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 text-sm font-semibold truncate">{user?.fullName || t("common.user")}</p>
                <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-[11px] text-muted-foreground">
                  <UserCircle2 className="h-3.5 w-3.5" />
                  {roleLabel}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate">{workspaceName}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              <div className="pt-1 flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">{t("common.plan")}: {planSummaryLabel}</span>
                {canUpgradePlan ? (
                  <button
                    type="button"
                    onClick={onUpgradePlanClick}
                    className="text-xs font-medium text-accent hover:underline"
                  >
                    {t("topbar.upgradePlan")}
                  </button>
                ) : null}
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <User className="mr-2 h-4 w-4" /> {t("topbar.profile")}
            </DropdownMenuItem>
            <div className="flex items-center justify-between gap-3 rounded-sm px-2 py-2">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <MessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{t("topbar.supportChat")}</span>
              </div>
              <Switch checked={supportChatEnabled} onCheckedChange={onToggleSupportChat} />
            </div>
            <div className="px-2 py-2">
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setConfirmLogoutOpen(true)}
            >
              <LogOut className="mr-2 h-4 w-4" /> {t("topbar.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu open={mobileNotificationOpen} onOpenChange={setMobileNotificationOpen}>
          <DropdownMenuTrigger asChild>
            <button className="relative p-2 rounded-md hover:bg-secondary" aria-label={t("topbar.notifications")}>
              <Bell className="h-5.5 w-5.5 text-muted-foreground" />
              {unreadCount > 0 ? <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-accent" /> : null}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={8}
            collisionPadding={12}
            className="w-80 max-w-[calc(100vw-1.5rem)] p-2"
          >
            <div className="px-2 py-1.5 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{t("topbar.notifications")}</p>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                disabled={markingAll || unreadCount === 0}
                onClick={() => void onMarkAllRead()}
              >
                <span className="inline-flex items-center gap-1">
                  <CheckCheck className="h-3.5 w-3.5" />
                  {t("topbar.markAllRead")}
                </span>
              </button>
            </div>
            <DropdownMenuSeparator />
            {loadingNotifications ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">{t("common.loading")}</div>
            ) : notifications.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">{t("topbar.noNotifications")}</div>
            ) : (
              <div className="max-h-80 overflow-auto">
                {notifications.map((item) => (
                  <div
                    key={item.id}
                    className="px-2 py-2 rounded-md hover:bg-secondary transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 shrink-0">{getNotificationIcon(item.type)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {!item.readAt ? <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" /> : null}
                          <button
                            type="button"
                            className={`min-w-0 text-left text-sm truncate ${item.readAt ? "font-medium" : "font-semibold"}`}
                            onClick={() => void onNotificationClick(item)}
                          >
                            {item.title}
                          </button>
                          {markingId === item.id ? <span className="text-[10px] text-muted-foreground">...</span> : null}
                        </div>
                        {item.body ? (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {item.body}
                            {item.dedupCount && item.dedupCount > 1 ? ` (${item.dedupCount}x)` : ""}
                          </p>
                        ) : null}
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <p className="text-[11px] text-muted-foreground">{relativeTime(item.createdAt)}</p>
                          {shouldShowApprovedTicketCta(item) ? (
                            <button
                              type="button"
                              className="shrink-0 rounded-full border border-accent/20 bg-accent/10 px-2.5 py-1 text-[11px] font-semibold text-accent hover:bg-accent/15"
                              onClick={() => void onScheduleFromNotification(item)}
                            >
                              {approvedTicketCtaLabel(item)}
                            </button>
                          ) : shouldShowPaymentCta(item) ? (
                            <button
                              type="button"
                              className="shrink-0 rounded-full border border-accent/20 bg-accent/10 px-2.5 py-1 text-[11px] font-semibold text-accent hover:bg-accent/15"
                              onClick={() => void onNotificationClick(item)}
                            >
                              View payments
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/approvals")} className="justify-between">
              <span>{t("nav.approvals")}</span>
              <ChevronRight className="h-4 w-4" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

      </div>

      <div className="flex-1 md:hidden" />

      <button
        onClick={() => setMobileMenuOpen(true)}
        className="md:hidden p-2 -mr-2 rounded-md hover:bg-secondary"
        aria-label={t("topbar.mobileMenu")}
      >
        <Menu className="h-6 w-6 text-foreground" />
      </button>

      <AlertDialog open={confirmLogoutOpen} onOpenChange={setConfirmLogoutOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("topbar.logout")}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t("topbar.logoutDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("login.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={onConfirmLogout}>
              {t("topbar.logout")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SlideOverPanel
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        title={t("topbar.mobileMenu")}
        className="w-full max-w-sm"
      >
        <div className="space-y-2">
          {mobileMenuItems.map((item) => (
            <button
              key={item.path}
              type="button"
              className="w-full flex items-center gap-3 rounded-lg border bg-card px-3 py-3 text-left hover:bg-muted/40 transition-colors"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate(item.path);
              }}
            >
              <item.icon className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium text-foreground">{item.label}</span>
            </button>
          ))}
        </div>
      </SlideOverPanel>
      <PlanApprovedWorkDialog
        open={Boolean(planWorkNotification)}
        onOpenChange={(open) => {
          if (!open) setPlanWorkNotification(null);
        }}
        context={
          planWorkNotification?.projectId
            ? {
                projectId: planWorkNotification.projectId,
                projectName: planWorkNotification.body || planWorkNotification.title,
                clientName: null,
                ticketNumber: planWorkNotification.title,
              }
            : null
        }
        onPlanned={() => {
          if (planWorkNotification) void markPlanningNotificationHandled(planWorkNotification);
        }}
      />
    </header>
  );
}
